import React, { useMemo, useState, useEffect, useRef } from 'react';
import '../styles/AssignCourseModal.css';
import { getPhonemeOptionsByLanguage } from '../list_data/phonemes';
import { supabase } from '../supabaseClient';
import { clearCourseDraftGeneratedContent, createCourseDraftRequest, updateCourseDraftContent } from '../api/supabaseAPI';
import Message from './Message';
import ConfirmDialog from './ConfirmDialog';

const AI_SKILL_OPTIONS = [
  { value: 'word_final_consonants', label: 'Word Final Consonants' },
  { value: 'clarity_in_multisyllables', label: 'Clarity in Multisyllables' },
  { value: 'consonant_clusters', label: 'Consonant Clusters' },
  { value: 'stress_patterns', label: 'Stress Patterns' },
];

const AI_ERROR_PRIORITY_OPTIONS = [
  { value: 'omission', label: 'Omission' },
  { value: 'substitution', label: 'Substitution' },
  { value: 'distortion', label: 'Distortion' },
];

const CEFR_VOCAB_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const KNOWN_AI_SKILL_VALUES = new Set(AI_SKILL_OPTIONS.map((item) => item.value));

const normalizeGeneratedSentences = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
};

export default function AssignCourseModal({ 
  isOpen, 
  onClose, 
  student, 
  onAssign,
  initialDraft = null,
  language = 'en_us',
}) {
  const [activeTab, setActiveTab] = useState('manual');
  
  // Form State
  const [courseName, setCourseName] = useState('');
  const [description, setDescription] = useState('');
  const [sentences, setSentences] = useState(['']); // Start with one empty sentence
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiStudentDescription, setAiStudentDescription] = useState('');
  const [aiGeneratingDescription, setAiGeneratingDescription] = useState('');
  const [aiVocabLevelHint, setAiVocabLevelHint] = useState('');
  const [aiPracticeCount, setAiPracticeCount] = useState('');
  const [aiMinWords, setAiMinWords] = useState('');
  const [aiMaxWords, setAiMaxWords] = useState('');
  const [aiFocusPhonemes, setAiFocusPhonemes] = useState('');
  const [aiSelectedPhonemes, setAiSelectedPhonemes] = useState([]);
  const [aiCustomPhonemeInput, setAiCustomPhonemeInput] = useState('');
  const [aiFocusSkills, setAiFocusSkills] = useState([]);
  const [aiErrorPriority, setAiErrorPriority] = useState([]);
  const [aiCustomSkillInput, setAiCustomSkillInput] = useState('');
  const [aiCustomSkills, setAiCustomSkills] = useState([]);
  const [aiDraftId, setAiDraftId] = useState(null);
  const [aiIsGenerating, setAiIsGenerating] = useState(false);
  const [aiGenerationError, setAiGenerationError] = useState('');
  const [aiGeneratedSentences, setAiGeneratedSentences] = useState([]);
  const [aiOriginalSentences, setAiOriginalSentences] = useState([]);
  const [draggedAiIndex, setDraggedAiIndex] = useState(null);
  const [dragOverAiIndex, setDragOverAiIndex] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showLockedTooltip, setShowLockedTooltip] = useState(false);
  const lockedSettingsRef = useRef(null);
  const [saveToast, setSaveToast] = useState({ text: '', type: '' });
  const [isAssignConfirmOpen, setIsAssignConfirmOpen] = useState(false);

  const phonemeOptions = useMemo(() => getPhonemeOptionsByLanguage(language), [language]);

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const defaultPhonemes = getPhonemeOptionsByLanguage(language).slice(0, 4);

    const resetAiDefaults = () => {
      setAiTopic('');
      setAiStudentDescription('');
      setAiGeneratingDescription('');
      setAiVocabLevelHint('');
      setAiPracticeCount('');
      setAiMinWords('');
      setAiMaxWords('');
      setAiFocusPhonemes('');
      setAiSelectedPhonemes([]);
      setAiCustomPhonemeInput('');
      setAiFocusSkills([]);
      setAiErrorPriority([]);
      setAiCustomSkillInput('');
      setAiCustomSkills([]);
      setAiDraftId(null);
      setAiIsGenerating(false);
      setAiGenerationError('');
      setAiGeneratedSentences([]);
      setAiOriginalSentences([]);
    };

    setIsSubmitting(false);
    setDescription('');
    setSentences(['']);

    if (!initialDraft) {
      setCourseName('');
      setActiveTab('manual');
      resetAiDefaults();
      return;
    }

    const requestJson = initialDraft?.request_json || {};
    const generatedJson = initialDraft?.generated_json || {};
    const internalMeta = requestJson?.internal_meta || {};
    const focus = internalMeta?.focus || {};
    const constraints = internalMeta?.generation_constraints || {};
    const draftLanguage = requestJson?.internal_meta?.target_language || language;
    const mergedPhonemes = Array.isArray(focus?.focus_phonemes) && focus.focus_phonemes.length > 0
      ? focus.focus_phonemes
      : defaultPhonemes;
    const allSkills = Array.isArray(focus?.focus_skills) ? focus.focus_skills : [];
    const presetSkills = allSkills.filter((skill) => KNOWN_AI_SKILL_VALUES.has(skill));
    const customSkills = allSkills.filter((skill) => !KNOWN_AI_SKILL_VALUES.has(skill));

    setActiveTab('ai');
    setCourseName(requestJson?.course_name || '');
    setAiTopic(requestJson?.topic || '');
    setAiStudentDescription(requestJson?.description || '');
    setAiGeneratingDescription(internalMeta?.generating_description || '');
    setAiVocabLevelHint(constraints?.vocab_level_hint || 'B1');
    setAiPracticeCount(Number(internalMeta?.practice_count || requestJson?.length_of_content || 70));
    setAiMinWords(Number(constraints?.sentence_length_words_range?.[0] || 7));
    setAiMaxWords(Number(constraints?.sentence_length_words_range?.[1] || 14));
    setAiSelectedPhonemes(mergedPhonemes);
    setAiFocusPhonemes(mergedPhonemes.join(', '));
    setAiFocusSkills(presetSkills);
    setAiErrorPriority(Array.isArray(focus?.error_types_priority) ? focus.error_types_priority : ['omission', 'substitution']);
    setAiCustomSkills(customSkills);
    setAiCustomSkillInput('');
    setAiCustomPhonemeInput('');
    setAiDraftId(initialDraft.id || null);
    setAiIsGenerating(false);
    setAiGenerationError('');

    const generatedContent = normalizeGeneratedSentences(generatedJson?.content);
    setAiGeneratedSentences(generatedContent);
    setAiOriginalSentences(generatedContent);
    setAiIsGenerating(generatedContent.length === 0);
    if (draftLanguage !== language) {
      setAiGenerationError(`Draft language (${draftLanguage}) differs from current workspace language (${language}).`);
    }
  }, [isOpen, language, initialDraft]);

  // Manual Content Handlers
  const handleAddSentence = () => {
    // Prevent adding if the last sentence is empty
    if (sentences.length > 0 && sentences[sentences.length - 1].trim() === '') {
      return;
    }
    setSentences([...sentences, '']);
  };

  const handleRemoveSentence = (index) => {
    const newSentences = sentences.filter((_, i) => i !== index);
    setSentences(newSentences.length ? newSentences : ['']); // Keep at least one
  };

  const handleSentenceChange = (index, value) => {
    const newSentences = [...sentences];
    newSentences[index] = value;
    setSentences(newSentences);
  };

  const toggleSelection = (current, value, setter) => {
    if (current.includes(value)) {
      setter(current.filter((item) => item !== value));
      return;
    }
    setter([...current, value]);
  };

  const handleAddCustomSkill = () => {
    const normalized = aiCustomSkillInput.trim().toLowerCase().replace(/\s+/g, '_');
    if (!normalized) return;
    if (aiFocusSkills.includes(normalized) || aiCustomSkills.includes(normalized)) {
      setAiCustomSkillInput('');
      return;
    }
    setAiCustomSkills((prev) => [...prev, normalized]);
    setAiCustomSkillInput('');
  };

  const handleRemoveCustomSkill = (skill) => {
    setAiCustomSkills((prev) => prev.filter((item) => item !== skill));
  };

  const handleTogglePhoneme = (phoneme) => {
    setAiSelectedPhonemes((prev) => (
      prev.includes(phoneme)
        ? prev.filter((item) => item !== phoneme)
        : [...prev, phoneme]
    ));
  };

  const handleAddCustomPhoneme = () => {
    const normalized = aiCustomPhonemeInput.trim();
    if (!normalized) return;
    setAiSelectedPhonemes((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
    setAiCustomPhonemeInput('');
  };

  const handleRemoveSelectedPhoneme = (phoneme) => {
    setAiSelectedPhonemes((prev) => prev.filter((item) => item !== phoneme));
  };

  const parsedPhonemes = useMemo(
    () => aiFocusPhonemes
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
    [aiFocusPhonemes],
  );

  useEffect(() => {
    const merged = Array.from(new Set([
      ...aiSelectedPhonemes.map((item) => item.trim()).filter(Boolean),
      ...parsedPhonemes,
    ]));
    const next = merged.join(', ');
    if (next !== aiFocusPhonemes) {
      setAiFocusPhonemes(next);
    }
  }, [aiSelectedPhonemes, parsedPhonemes, aiFocusPhonemes]);

  const aiRequestPayload = useMemo(() => ({
    content: null,
    course_name: courseName || 'AI Generated Course',
    topic: aiTopic || '',
    description: aiStudentDescription || `Targeted pronunciation practice for ${(aiTopic || 'selected speaking goals').toLowerCase()}.`,
    internal_meta: {
      focus: {
        focus_skills: [...aiFocusSkills, ...aiCustomSkills],
      focus_phonemes: aiSelectedPhonemes,
        error_types_priority: aiErrorPriority,
      },
      source: 'tutor_ai',
      internal_only: true,
      practice_count: Number(aiPracticeCount),
      target_language: language,
      generating_description: aiGeneratingDescription || 'Generate practice sentences based on the selected constraints.',
      generation_constraints: {
        vocab_level_hint: aiVocabLevelHint,
        sentence_length_words_range: [Number(aiMinWords), Number(aiMaxWords)],
      },
    },
    length_of_content: Number(aiPracticeCount),
  }), [
    courseName,
    aiStudentDescription,
    aiTopic,
    aiFocusSkills,
    aiCustomSkills,
    aiSelectedPhonemes,
    aiErrorPriority,
    aiGeneratingDescription,
    aiVocabLevelHint,
    aiPracticeCount,
    language,
    aiMinWords,
    aiMaxWords,
  ]);

  const aiValidationMissing = useMemo(() => {
    const missing = [];
    if (!courseName.trim()) missing.push('Course Name');
    if (!aiStudentDescription.trim()) missing.push('Student Description');
    if (!aiTopic.trim()) missing.push('Topic');
    if (!aiGeneratingDescription.trim()) missing.push('AI Generating Description');
    const practiceCountNum = Number(aiPracticeCount);
    const minWordsNum = Number(aiMinWords);
    const maxWordsNum = Number(aiMaxWords);
    if (!aiVocabLevelHint.trim()) missing.push('Vocab Level Hint');
    if (!Number.isInteger(practiceCountNum) || practiceCountNum < 5) missing.push('Practice Count (>= 5)');
    if (!Number.isInteger(minWordsNum) || minWordsNum < 3) missing.push('Min Words (>= 3)');
    if (!Number.isInteger(maxWordsNum) || maxWordsNum < 3) missing.push('Max Words (>= 3)');
    if (Number.isInteger(minWordsNum) && Number.isInteger(maxWordsNum) && minWordsNum > maxWordsNum) {
      missing.push('Min Words must be <= Max Words');
    }
    if (aiSelectedPhonemes.length === 0) missing.push('Focus Phonemes');
    if (aiFocusSkills.length + aiCustomSkills.length === 0) missing.push('Focus Skills');
    if (aiErrorPriority.length === 0) missing.push('Error Priority');
    return missing;
  }, [
    courseName,
    aiStudentDescription,
    aiTopic,
    aiGeneratingDescription,
    aiPracticeCount,
    aiMinWords,
    aiMaxWords,
    aiSelectedPhonemes,
    aiFocusSkills,
    aiCustomSkills,
    aiErrorPriority,
  ]);

  const aiCanGenerate = aiValidationMissing.length === 0;
  const aiIsLocked = aiGeneratedSentences.length > 0;

  const updateLockedTooltipPosition = (event) => {
    const container = lockedSettingsRef.current;
    if (!container) return;

    const bounds = container.getBoundingClientRect();
    const offsetX = event.clientX - bounds.left;
    const offsetY = event.clientY - bounds.top;

    container.style.setProperty('--locked-tooltip-x', `${offsetX}px`);
    container.style.setProperty('--locked-tooltip-y', `${offsetY}px`);
  };

  const handleLockedOverlayEnter = (event) => {
    updateLockedTooltipPosition(event);
    setShowLockedTooltip(true);
  };

  const handleLockedOverlayMove = (event) => {
    updateLockedTooltipPosition(event);
    if (!showLockedTooltip) {
      setShowLockedTooltip(true);
    }
  };

  const handleLockedOverlayLeave = () => {
    setShowLockedTooltip(false);
  };

  const handleLockedOverlayPointerDown = (event) => {
    updateLockedTooltipPosition(event);
    setShowLockedTooltip(true);
  };
  useEffect(() => {
    if (!isOpen || !aiDraftId) return undefined;

    const channelName = `course-draft-${aiDraftId}-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'course_generation_drafts',
          filter: `id=eq.${aiDraftId}`,
        },
        (payload) => {
          const generated = payload?.new?.generated_json;
          const generatedContent = normalizeGeneratedSentences(generated?.content);
          if (generatedContent.length > 0) {
            setAiIsGenerating(false);
            setAiGenerationError('');
            setAiGeneratedSentences(generatedContent);
            setAiOriginalSentences(generatedContent);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, aiDraftId]);

  useEffect(() => {
    if (!isOpen || !aiDraftId || !aiIsGenerating) return undefined;

    const intervalId = window.setInterval(async () => {
      const { data, error } = await supabase
        .from('course_generation_drafts')
        .select('generated_json')
        .eq('id', aiDraftId)
        .maybeSingle();

      if (error || !data) return;

      const generated = data.generated_json;
      const generatedContent = normalizeGeneratedSentences(generated?.content);
      if (generatedContent.length > 0) {
        setAiIsGenerating(false);
        setAiGenerationError('');
        setAiGeneratedSentences(generatedContent);
        setAiOriginalSentences(generatedContent);
      }
    }, 2000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen, aiDraftId, aiIsGenerating]);

  useEffect(() => {
    if (!aiIsGenerating) return undefined;
    const timeoutId = window.setTimeout(() => {
      setAiIsGenerating(false);
      setAiGenerationError('Generation timeout. Please try again.');
    }, 60000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [aiIsGenerating]);

  const handleAiSentenceChange = (index, value) => {
    setAiGeneratedSentences((prev) => prev.map((sentence, i) => (i === index ? value : sentence)));
  };

  const handleAiDragStart = (e, index) => {
    setDraggedAiIndex(index);
    setDragOverAiIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    }
  };

  const handleAiDragEnter = (e, targetIndex) => {
    e.preventDefault();
    if (draggedAiIndex === null) return;
    if (dragOverAiIndex !== targetIndex) {
      setDragOverAiIndex(targetIndex);
    }
  };

  const handleAiDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleAiDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedAiIndex === null) {
      setDragOverAiIndex(null);
      return;
    }

    setAiGeneratedSentences((prev) => {
      const next = [...prev];
      const current = next[draggedAiIndex];
      next.splice(draggedAiIndex, 1); // Remove from old position
      
      // Calculate correct insertion index based on dragging direction
      let insertIndex = dropIndex;
      if (draggedAiIndex < dropIndex) {
        insertIndex = dropIndex - 1;
      }

      next.splice(insertIndex, 0, current); // Insert at new position
      return next;
    });

    setDraggedAiIndex(null);
    setDragOverAiIndex(null);
  };

  const handleAiDragEnd = () => {
    setDraggedAiIndex(null);
    setDragOverAiIndex(null);
  };

  const removeAiSentence = (index) => {
    setAiGeneratedSentences((prev) => prev.filter((_, i) => i !== index));
  };

  const addAiSentence = () => {
    setAiGeneratedSentences((prev) => [...prev, '']);
  };

  const restoreAiGenerated = () => {
    setAiGeneratedSentences([...aiOriginalSentences]);
  };

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  // ... (inside the component)

  const handleClearClick = () => {
    setIsClearConfirmOpen(true);
  };

  const confirmClearAiGeneratedResult = async () => {
    setIsClearConfirmOpen(false);
    if (!aiDraftId) {
      setAiGeneratedSentences([]);
      setAiOriginalSentences([]);
      return;
    }
    try {
      await clearCourseDraftGeneratedContent(aiDraftId, aiRequestPayload);
      setAiGeneratedSentences([]);
      setAiOriginalSentences([]);
      setAiGenerationError('');
      setAiIsGenerating(false);
    } catch (error) {
      setAiGenerationError(error?.message || 'Failed to clear generated result.');
    }
  };

  const handleGenerateAiSentences = async () => {
    if (!student?.student_id || aiIsGenerating || !aiCanGenerate) return;
    
    // Backup current sentences before generating
    const previousSentences = [...aiGeneratedSentences];
    const previousOriginal = [...aiOriginalSentences];
    const previousDraftId = aiDraftId;
    
    setAiIsGenerating(true);
    setAiGenerationError('');
    setAiGeneratedSentences([]);
    setAiOriginalSentences([]);
    setAiDraftId(null);

    try {
      const result = await createCourseDraftRequest({
        studentId: student.student_id,
        language,
        requestJson: aiRequestPayload,
        draftId: previousDraftId,
      });
      const draftId = result?.draft_id ?? result?.result?.draft_id ?? null;
      if (!draftId) {
        throw new Error('Draft created but draft_id is missing.');
      }
      setAiDraftId(draftId);
    } catch (error) {
      setAiIsGenerating(false);
      setAiGenerationError(error?.message || 'Failed to generate draft.');
      // Rollback to previous state
      setAiGeneratedSentences(previousSentences);
      setAiOriginalSentences(previousOriginal);
      setAiDraftId(previousDraftId);
    }
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (saveToast.text) {
      const timer = setTimeout(() => {
        setSaveToast({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveToast]);

  const handleSaveDraft = async () => {
    if (!aiDraftId) return;
    setIsSavingDraft(true);
    setSaveToast({ text: '', type: '' });

    try {
      const currentSentences = aiGeneratedSentences.map((s) => s.trim()).filter(Boolean);
      
      // Check if current sentences and basic details are exactly the same as the original ones
      const initialDraftJson = initialDraft?.request_json || {};
      const isContentSame = 
        currentSentences.length === aiOriginalSentences.length && 
        currentSentences.every((sentence, index) => sentence === aiOriginalSentences[index]);
      const isNameSame = courseName === (initialDraftJson.course_name || '');
      const isDescriptionSame = aiStudentDescription === (initialDraftJson.description || '');

      if (isContentSame && isNameSame && isDescriptionSame) {
        setSaveToast({ text: 'No changes to save.', type: 'info' });
        setIsSavingDraft(false);
        return;
      }

      await updateCourseDraftContent(aiDraftId, currentSentences, courseName, aiStudentDescription);
      
      // We only update original sentences state, the name and description are bound to their input states anyway
      setAiOriginalSentences(currentSentences);
      
      setSaveToast({ text: 'Draft saved successfully.', type: 'success' });
    } catch (error) {
      console.error("Failed to save draft:", error);
      setSaveToast({ text: error?.message || 'Failed to save draft.', type: 'error' });
    } finally {
      setIsSavingDraft(false);
    }
  };

  if (!isOpen) return null;

  // Validation
  const isValid = () => {
    if (activeTab === 'manual') {
      const hasValidName = courseName.trim().length > 0;
      const hasValidSentences = sentences.some(s => s.trim().length > 0);
      return hasValidName && hasValidSentences;
    }
    const hasValidName = courseName.trim().length > 0;
    const hasValidSentences = aiGeneratedSentences.some((s) => s.trim().length > 0);
    return hasValidName && hasValidSentences;
  };

  const performSubmit = async () => {
    if (!isValid() || isSubmitting) return;

    setIsSubmitting(true);
    
    // Prepare payload based on active tab
    const payload = {
      type: activeTab,
      studentId: student?.student_id, // Use student_id (the user's UUID)
      draftId: activeTab === 'ai' ? aiDraftId : null,
      courseName,
      description: activeTab === 'manual' ? description : aiStudentDescription,
      sentences: activeTab === 'manual'
        ? sentences.filter(s => s.trim().length > 0)
        : aiGeneratedSentences.map((s) => s.trim()).filter(Boolean),
    };

    try {
      if (onAssign) {
        await onAssign(payload);
      }
      onClose();
    } catch (error) {
      console.error("Assignment failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!isValid() || isSubmitting) return;

    if (activeTab === 'ai' && aiDraftId) {
      setIsAssignConfirmOpen(true);
      return;
    }

    await performSubmit();
  };

  const confirmAssignDraft = async () => {
    setIsAssignConfirmOpen(false);
    await performSubmit();
  };

  return (
    <div className="assign-course-overlay" onClick={onClose}>
      <div className="assign-course-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Toast Container */}
        {saveToast.text && (
          <div className="assign-course-toast-container">
            <div className="toast-item">
              <Message text={saveToast.text} type={saveToast.type} mode="toast" />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="assign-course-header">
          <h2 className="assign-course-title">
            Assign Course to {student?.username || 'Student'}
          </h2>
          <button type="button" className="assign-course-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="assign-course-tabs">
          <button 
            type="button"
            className={`assign-course-tab ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveTab('manual')}
          >
            Manual Content
          </button>
          <button 
            type="button"
            className={`assign-course-tab ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            AI Generated
          </button>
        </div>

        {/* Body */}
        <div className="assign-course-body">
          {activeTab === 'manual' ? (
            <div className="manual-content-form">
              <div className="form-group">
                <label className="form-label">Course Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g., Week 1 Pronunciation Practice" 
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description (Optional)</label>
                <textarea 
                  className="form-textarea" 
                  placeholder="Add a note or instructions for the student..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="sentences-section">
                <div className="sentences-header">
                  <span className="sentences-title">Practice Sentences</span>
                  <button 
                    type="button"
                    className={`add-sentence-btn ${sentences.length > 0 && sentences[sentences.length - 1].trim() === '' ? 'is-disabled' : ''}`}
                    onClick={handleAddSentence}
                    disabled={sentences.length > 0 && sentences[sentences.length - 1].trim() === ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Add Sentence
                  </button>
                </div>
                
                {sentences.length === 0 && (
                  <div className="empty-sentences-hint">
                    No sentences added. Click "Add Sentence" to start.
                  </div>
                )}

                {sentences.map((sentence, index) => (
                  <div key={index} className="sentence-item">
                    <input 
                      type="text" 
                      className="form-input sentence-input" 
                      placeholder={`Sentence ${index + 1}`}
                      value={sentence}
                      onChange={(e) => handleSentenceChange(index, e.target.value)}
                    />
                    <button 
                      type="button"
                      className="remove-sentence-btn"
                      onClick={() => handleRemoveSentence(index)}
                      title="Remove"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`ai-content-form ${aiIsLocked ? 'is-locked' : ''}`}>
              <div className="form-group">
                <label className="form-label">Course Name</label>                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Perfect Endings: Multisyllable Clarity"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Student Description</label>
                <textarea
                  className="form-textarea"
                  placeholder="This description will be visible to the student."
                  value={aiStudentDescription}
                  onChange={(e) => setAiStudentDescription(e.target.value)}
                />
              </div>

              <div
                ref={lockedSettingsRef}
                className="ai-generation-settings-container"
              >
                {aiIsLocked && (
                  <div
                    className="ai-locked-overlay-note"
                    onMouseEnter={handleLockedOverlayEnter}
                    onMouseMove={handleLockedOverlayMove}
                    onMouseLeave={handleLockedOverlayLeave}
                    onMouseDown={handleLockedOverlayPointerDown}
                  />
                )}
                {aiIsLocked && showLockedTooltip && (
                  <div className="ai-locked-tooltip" role="status" aria-live="polite">
                    Settings are locked. Clear generated results to edit them again.
                  </div>
                )}

                <div className="ai-two-col-grid">
                  <div className="form-group">
                    <label className="form-label">Topic</label>
                    <input
                      type="text"
                      className="form-input"
                      value={aiTopic}
                      onChange={(e) => setAiTopic(e.target.value)}
                      placeholder="e.g. Daily communication, school presentation, interview"
                      disabled={aiIsLocked}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vocab Level Hint (CEFR)</label>
                    <select
                      className="form-input"
                      value={aiVocabLevelHint}
                      onChange={(e) => setAiVocabLevelHint(e.target.value)}
                      disabled={aiIsLocked}
                    >
                      <option value="" disabled>Select CEFR level</option>
                      {CEFR_VOCAB_LEVELS.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="ai-three-col-grid">
                  <div className="form-group">
                    <label className="form-label">Practice Count</label>
                    <input
                      type="number"
                      className="form-input"
                      min="5"
                      max="200"
                      value={aiPracticeCount}
                      onChange={(e) => setAiPracticeCount(Number(e.target.value || 0))}
                      disabled={aiIsLocked}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Min Words</label>
                    <input
                      type="number"
                      className="form-input"
                      min="3"
                      max="30"
                      value={aiMinWords}
                      onChange={(e) => setAiMinWords(Number(e.target.value || 0))}
                      disabled={aiIsLocked}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Words</label>
                    <input
                      type="number"
                      className="form-input"
                      min="3"
                      max="40"
                      value={aiMaxWords}
                      onChange={(e) => setAiMaxWords(Number(e.target.value || 0))}
                      disabled={aiIsLocked}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Focus Phonemes</label>
                  <div className="ai-chip-list ai-phoneme-chip-list">
                    {phonemeOptions.map((phoneme) => (
                      <button
                        key={phoneme}
                        type="button"
                        className={`ai-chip ${aiSelectedPhonemes.includes(phoneme) ? 'active' : ''}`}
                        onClick={() => handleTogglePhoneme(phoneme)}
                        disabled={aiIsLocked}
                      >
                        {phoneme}
                      </button>
                    ))}
                  </div>
                  <div className="ai-custom-skill-row">
                    <input
                      type="text"
                      className="form-input"
                      value={aiCustomPhonemeInput}
                      onChange={(e) => setAiCustomPhonemeInput(e.target.value)}
                      placeholder="Custom phoneme (e.g. ɝ, aɪ, oʊ)"
                      disabled={aiIsLocked}
                    />
                    <button
                      type="button"
                      className="ai-custom-add-btn"
                      onClick={handleAddCustomPhoneme}
                      disabled={aiIsLocked}
                    >
                      Add
                    </button>
                  </div>
                  {aiSelectedPhonemes.length > 0 && (
                    <div className="ai-chip-list ai-custom-skill-list ai-selected-phoneme-list">
                      {aiSelectedPhonemes.map((phoneme) => (
                        <button
                          key={phoneme}
                          type="button"
                          className="ai-chip active"
                          onClick={() => handleRemoveSelectedPhoneme(phoneme)}
                          title="Click to remove"
                          disabled={aiIsLocked}
                        >
                          {phoneme}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Focus Skills</label>
                  <div className="ai-chip-list">
                    {AI_SKILL_OPTIONS.map((skill) => (
                      <button
                        key={skill.value}
                        type="button"
                        className={`ai-chip ${aiFocusSkills.includes(skill.value) ? 'active' : ''}`}
                        onClick={() => toggleSelection(aiFocusSkills, skill.value, setAiFocusSkills)}
                        disabled={aiIsLocked}
                      >
                        {skill.label}
                      </button>
                    ))}
                  </div>
                  <div className="ai-custom-skill-row">
                    <input
                      type="text"
                      className="form-input"
                      value={aiCustomSkillInput}
                      onChange={(e) => setAiCustomSkillInput(e.target.value)}
                      placeholder="Custom skill (e.g. syllable_stress_control)"
                      disabled={aiIsLocked}
                    />
                    <button
                      type="button"
                      className="ai-custom-add-btn"
                      onClick={handleAddCustomSkill}
                      disabled={aiIsLocked}
                    >
                      Add
                    </button>
                  </div>
                  {aiCustomSkills.length > 0 && (
                    <div className="ai-chip-list ai-custom-skill-list">
                      {aiCustomSkills.map((skill) => (
                        <button
                          key={skill}
                          type="button"
                          className="ai-chip active"
                          onClick={() => handleRemoveCustomSkill(skill)}
                          title="Click to remove"
                          disabled={aiIsLocked}
                        >
                          {skill}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Error Priority</label>
                  <div className="ai-chip-list">
                    {AI_ERROR_PRIORITY_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`ai-chip ${aiErrorPriority.includes(option.value) ? 'active' : ''}`}
                        onClick={() => toggleSelection(aiErrorPriority, option.value, setAiErrorPriority)}
                        disabled={aiIsLocked}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">AI Generating Description (Internal)</label>
                  <textarea
                    className="form-textarea ai-generating-textarea"
                    placeholder="Detailed prompt for AI generation. This is internal and not shown to students."
                    value={aiGeneratingDescription}
                    onChange={(e) => setAiGeneratingDescription(e.target.value)}
                    disabled={aiIsLocked}
                  />
                </div>
              </div>

              <div className="ai-generate-row">                <button
                  type="button"
                  className="btn-generate-ai"
                  onClick={handleGenerateAiSentences}
                  disabled={aiIsGenerating || !aiCanGenerate || aiIsLocked}
                >
                  {aiIsGenerating ? 'Generating...' : 'Generate Sentences'}
                </button>
              </div>
              {!aiCanGenerate && (
                <div className="ai-validation-note">
                  Complete required fields: {aiValidationMissing.join(', ')}
                </div>
              )}

              {aiGenerationError && (
                <div className="ai-generate-error">
                  {aiGenerationError}
                </div>
              )}

              {!aiIsGenerating && aiGeneratedSentences.length === 0 && !aiGenerationError && (
                <div className="ai-result-empty">
                  Generated content will appear here after you click "Generate Sentences".
                </div>
              )}

              {aiGeneratedSentences.length > 0 && (
                <div className="ai-result-editor">
                  <div className="ai-preview-header">
                    <span className="sentences-title">Generated Sentences ({aiGeneratedSentences.length})</span>
                    <div className="ai-result-actions">
                      <button
                        type="button"
                        className="ai-editor-btn"
                        onClick={addAiSentence}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        className="ai-editor-btn"
                        onClick={restoreAiGenerated}
                        disabled={aiOriginalSentences.length === 0}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        className="ai-editor-btn"
                        onClick={handleClearClick}
                      >
                        Clear Result
                      </button>
                    </div>                  </div>

                  <div className={`ai-generated-list ${draggedAiIndex !== null ? 'is-dragging-list' : ''}`}>
                    {aiGeneratedSentences.map((sentence, index) => (
                      <div 
                        key={`ai-sentence-${index}`} 
                        className={`ai-generated-item ${draggedAiIndex === index ? 'is-dragging' : ''} ${dragOverAiIndex === index && draggedAiIndex !== index ? 'is-drag-over' : ''}`}
                        onDragEnter={(e) => handleAiDragEnter(e, index)}
                        onDragOver={handleAiDragOver}
                        onDrop={(e) => handleAiDrop(e, index)}
                      >
                        <div 
                          className="ai-drag-handle" 
                          title="Drag to reorder"
                          draggable
                          onDragStart={(e) => handleAiDragStart(e, index)}
                          onDragEnd={handleAiDragEnd}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="9" cy="5" r="1.5"></circle>
                            <circle cx="15" cy="5" r="1.5"></circle>
                            <circle cx="9" cy="12" r="1.5"></circle>
                            <circle cx="15" cy="12" r="1.5"></circle>
                            <circle cx="9" cy="19" r="1.5"></circle>
                            <circle cx="15" cy="19" r="1.5"></circle>
                          </svg>
                        </div>
                        <div className="ai-generated-index">{index + 1}</div>
                        <textarea
                          className="form-textarea ai-generated-textarea"
                          value={sentence}
                          onChange={(e) => handleAiSentenceChange(index, e.target.value)}
                          placeholder={`Sentence ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="ai-remove-btn"
                          onClick={() => removeAiSentence(index)}
                          title="Remove"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                          </button>
                          </div>
                          ))}
                          {/* Invisible drop zone to allow moving items to the very end */}
                          {draggedAiIndex !== null && (
                          <div
                          className={`ai-bottom-drop-zone ${dragOverAiIndex === aiGeneratedSentences.length ? 'is-drag-over' : ''}`}
                          onDragEnter={(e) => handleAiDragEnter(e, aiGeneratedSentences.length)}
                          onDragOver={handleAiDragOver}
                          onDrop={(e) => handleAiDrop(e, aiGeneratedSentences.length)}
                          />
                          )}
                          </div>
                          </div>
                          )}            </div>
          )}
        </div>

        {/* Footer */}
        <div className="assign-course-footer">
          {activeTab === 'ai' && aiDraftId && aiGeneratedSentences.length > 0 && (
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={handleSaveDraft} 
              disabled={isSavingDraft || isSubmitting}
            >
              {isSavingDraft ? 'Saving...' : 'Save Draft'}
            </button>
          )}
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button 
            type="button"
            className="btn-submit" 
            onClick={handleSubmit} 
            disabled={!isValid() || isSubmitting}
          >
            {isSubmitting ? 'Assigning...' : 'Assign Course'}
          </button>
        </div>
      </div>
      
      {/* Confirm Clear Result Dialog */}
      <ConfirmDialog
        isOpen={isClearConfirmOpen}
        title="Clear Generated Result?"
        message="Are you sure you want to clear the current results? All manual edits will be lost."
        onConfirm={confirmClearAiGeneratedResult}
        onCancel={() => setIsClearConfirmOpen(false)}
      />
      <ConfirmDialog
        isOpen={isAssignConfirmOpen}
        title="Assign Draft as Course?"
        message="Are you sure you want to assign this draft as a course? This will move it into Courses and remove the draft."
        onConfirm={confirmAssignDraft}
        onCancel={() => setIsAssignConfirmOpen(false)}
      />
    </div>
  );
}
